import React, { useContext } from 'react'
import styled, { css } from 'styled-components'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import Container from '../system/container/container'
import EventSidebar from './sidebar/event-sidebar'
import EventFAQ from './event-faq'
import { fonts, colors } from '../../styles/theme'
import Subheadline from '../system/typography/subheadline'
import breakpoints from '../../styles/breakpoints'
import { bumpRichTextHeading } from '../../utils/rich-text-utils'
import PACEvent from '../../models/event/pac-event'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import { EventStatus } from '../../models/event/event-status'
import LiveBug from '../stream-popout/live-bug'
import { useIsLive } from '../stream-popout/stream-provider'

const StyledEventDetails = styled(Container)`
  display: grid;
  grid-template-areas:
    'title'
    'sidebar'
    'about'
    'faq';
  grid-column-gap: ${props => props.theme.spacing.base600};
  margin-top: ${props => props.theme.spacing.base600};
  margin-bottom: ${props => props.theme.spacing.base600};
  grid-template-rows: max-content;

  @media (min-width: ${breakpoints.lg}) {
    grid-template-areas:
      'title  sidebar'
      'about  sidebar'
      'faq    faq';

    grid-template-columns: 3fr 2fr;
    grid-template-rows: auto 1fr auto;
  }

  @media (min-width: ${breakpoints.xxl}) {
    ${({ ticketed }: { ticketed: boolean }) =>
      ticketed
        ? css`
            grid-template-columns: 2fr 1fr;
          `
        : css`
            grid-template-columns: 5fr 2fr;
          `}
  }
`

const Title = styled.div`
  grid-area: title;
  margin-bottom: ${props => props.theme.spacing.base400};

  h1 {
    margin-top: 0;
    margin-bottom: ${props => props.theme.spacing.base400};
  }

  ${Subheadline} {
    line-height: 1.115em;
  }
`

const About = styled.section`
  grid-area: about;

  ul {
    list-style: unset;
    padding-left: 40px;
  }

  h3,
  h4,
  h5,
  h6 {
    font-family: ${fonts.raleway};
    text-transform: uppercase;
  }

  h5,
  h6 {
    margin-top: 1rem;
    margin-bottom: 1rem;
  }
`

const Sidebar = styled(EventSidebar)`
  grid-area: sidebar;
  align-self: start;
`

const FAQ = styled(EventFAQ)`
  grid-area: faq;
`

const NoDescription = styled.p`
  font-style: italic;
`

const AlertDescription = styled(NoDescription)`
  color: ${colors.alert};
`

const eventContext = React.createContext<PACEvent | null>(null)

export const useEvent = () => {
  return useContext(eventContext)
}

const Live = styled.div`
  color: ${colors.alert};
  font-weight: bold;
  text-transform: uppercase;
`

const EventDetails = ({ event }: { event: PACEvent }) => {
  const isLive = useIsLive(event)
  const hasDescription = !!documentToPlainTextString(event.description)
  const headingBumpedDescription = bumpRichTextHeading(2, event.description)

  return (
    <eventContext.Provider value={event}>
      <StyledEventDetails as="section" ticketed={event.ticketed}>
        <Title>
          <h1>{event.name}</h1>
          <Subheadline>Presented By {event.organizer.name}</Subheadline>
          {isLive && (
            <Live>
              <LiveBug /> Live Now!
            </Live>
          )}
        </Title>
        <About>
          <h2>About this event</h2>

          {event.statusAlertText && (
            <AlertDescription>
              <b>{event.statusAlertText}</b>
            </AlertDescription>
          )}

          {hasDescription
            ? documentToReactComponents(headingBumpedDescription)
            : event.status === EventStatus.ACTIVE && (
                <NoDescription>
                  More details about the event are on the way. Check back soon!
                </NoDescription>
              )}
        </About>
        <Sidebar event={event} />
        <FAQ faqs={event.faqs} />
      </StyledEventDetails>
    </eventContext.Provider>
  )
}

export default EventDetails
